<script>

    import { toast } from 'bulma-toast'
    import { invoicesStore } from '../stores'

    import InvoicesService from '../$services/facturama.services'
    import DateMethods from '../$methods/date.methods'
    import NumeralMethods from '../$methods/numeral.methods'

    import Button from '../$components/button.svelte'
    import Search from '../$components/input.search.svelte'
    import Select from '../$components/select.svelte'
    import Table from '../$components/table.svelte'

    // Facturama puede devolver la clave del SAT sola, con su descripción o solo el texto
    const PAYMENT_TYPES = {
        '01': 'Efectivo',
        '03': 'Transferencia',
        '04': 'Tarjeta de crédito',
        '28': 'Tarjeta de débito',
    }

    let loading = false
    let element = null
    let metadata = {}
    let lastRequest = 0
    let query = {
        admin: true,
        page: 0,
        find: null,
        status: '',
    }

    getInvoices()

    async function getInvoices() {

        const request = ++lastRequest

        loading = true
        const resp = await InvoicesService.getInvoices(query)

        // El usuario pudo cambiar de filtro mientras llegaba esta respuesta
        if(request !== lastRequest)
            return

        loading = false

        if(!resp.success)
            return toast({message: resp.error.message, type: 'is-danger'})

        invoicesStore.set(resp.data.invoices)
        metadata = resp.data.metadata
    }

    function applyFilters() {
        query.page = 0
        getInvoices()
    }

    function paymentLabel(type) {
        const code = String(type || '').match(/^\d{2}/)?.[0]

        return PAYMENT_TYPES[code] || type || '-'
    }

    async function getInvoiceDocument(invoice, type) {

        const blob = await InvoicesService.getInvoiceDocument(invoice.invoiceId, type)

        if(!blob)
            return toast({message: 'Error al descargar la factura', type: 'is-danger'})

        const url = window.URL.createObjectURL(blob)
        element.download = `${invoice.rfc}.${DateMethods.dateShort(invoice.created)}.${type}`
        element.href = url
        element.click()
        window.URL.revokeObjectURL(url)
    }

</script>

<a bind:this={element} href="/" style="display: none;">.</a>

<Table
    items={ $invoicesStore.length }
    total={ metadata.total }
    { loading }
    skeletonCols={7}
    bind:metadata
    bind:page={ query.page }
    on:change={ getInvoices }
>
    <div slot="header" class="header-filters">
        <div class="header-top">
            <Search bind:value={ query.find } on:enter={ applyFilters } placeholder="Buscar por usuario, RFC o razón social" />
        </div>
        <div class="header-actions invoices-filters">
            <Select bind:value={ query.status } on:change={ applyFilters }
                text="Todas"
                size="small"
                options={[
                    {value: 'active', text: 'Vigentes'},
                    {value: 'canceled', text: 'Canceladas'},
                ]}
            />
        </div>
    </div>
    <thead slot="thead">
        <tr>
            <th>Usuario</th>
            <th>Factura</th>
            <th>Forma de pago</th>
            <th>Estatus</th>
            <th>Fecha</th>
            <th class="align-right">Monto</th>
            <th class="align-right">Documentos</th>
        </tr>
    </thead>
    <tbody slot="tbody">
        {#each $invoicesStore as invoice (invoice._id)}
            <tr>
                <td>
                    <div>
                        <div class="strong">{ invoice.user?.name || 'Sin usuario' }</div>
                        {#if invoice.user?.email}
                            <div class="muted">{ invoice.user.email }</div>
                        {/if}
                    </div>
                </td>
                <td>
                    <div>
                        <div class="strong">{ invoice.name || invoice.rfc }</div>
                        {#if invoice.name}
                            <div class="muted">{ invoice.rfc }</div>
                        {/if}
                    </div>
                </td>
                <td>{ paymentLabel(invoice.paymentType) }</td>
                <td>
                    <span class="status-pill" class:is-canceled={invoice.status === 'canceled'}>
                        { invoice.status === 'canceled' ? 'Cancelada' : 'Vigente' }
                    </span>
                </td>
                <td title={DateMethods.dateTimeLarge(invoice.created)}>
                    <div>
                        <div>{ DateMethods.dateLarge(invoice.created) }</div>
                        <div class="muted">{ DateMethods.hourShort(invoice.created) }</div>
                    </div>
                </td>
                <td class="align-right">
                    <span class="amount">{ NumeralMethods.cash(invoice.amount) }</span>
                </td>
                <td class="align-right">
                    <div class="invoice-actions">
                        <Button on:click={() => getInvoiceDocument(invoice, 'xml')} text="XML" icon="file-code" size="small" tooltip="Descargar XML" />
                        <Button on:click={() => getInvoiceDocument(invoice, 'pdf')} text="PDF" icon="file-pdf" size="small" tooltip="Descargar PDF" />
                    </div>
                </td>
            </tr>
        {/each}
    </tbody>
</Table>

<style>
    .invoices-filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.75rem;
    }

    .strong {
        font-weight: 600;
        color: #111827;
        overflow-wrap: anywhere;
    }

    .muted {
        font-size: 0.85rem;
        color: #6b7280;
        overflow-wrap: anywhere;
    }

    .status-pill {
        display: inline-block;
        padding: 0.125rem 0.55rem;
        border-radius: 999px;
        background: #dcfce7;
        color: #166534;
        font-size: 0.75rem;
        font-weight: 600;
        line-height: 1.4;
        white-space: nowrap;
    }

    .status-pill.is-canceled {
        background: #fee2e2;
        color: #b91c1c;
    }

    .amount {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: #111827;
        white-space: nowrap;
    }

    .align-right {
        text-align: right;
    }

    /* El encabezado de la tabla alinea a la izquierda con una regla más específica */
    thead tr th.align-right {
        text-align: right;
    }

    .invoice-actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 0.35rem;
    }

    .invoice-actions :global(.button) {
        margin-right: 0;
    }

    /* Con el menú lateral abierto queda poco ancho: compactamos para no salirnos de la tabla */
    @media (min-width: 769px) and (max-width: 1100px) {
        :global(.premium-table) thead th,
        :global(.premium-table) tbody td {
            padding-left: 0.4rem;
            padding-right: 0.4rem;
        }
    }
</style>
